import { useState } from "react";
import {
  Button,
  Dialog,
  DialogText,
  FailedCallout,
  ReasonField,
  useAppNavigate,
  useReason,
  useToast,
  useCommandMutation,
} from "@amber/ui";
import type { ProfileDetail } from "../../contracts/index";
import { useProfileInvalidation } from "../invalidation";
import { profileCommands } from "../queries";

export type LifecycleKind = "retire" | "reactivate" | "delete";

const TEXT: Record<LifecycleKind, { title: (name: string) => string; done: (name: string) => string; action: string }> =
  {
    retire: { title: (name) => `Retire ${name}`, done: (name) => `${name} retired`, action: "Retire" },
    reactivate: { title: (name) => `Reactivate ${name}`, done: (name) => `${name} reactivated`, action: "Reactivate" },
    delete: {
      title: (name) => `Delete draft ${name}`,
      done: (name) => `Draft deleted: ${name}`,
      action: "Delete draft",
    },
  };

function ReactivateText({
  name,
  catchUp,
  setCatchUp,
}: {
  name: string;
  catchUp: boolean;
  setCatchUp: (next: boolean) => void;
}) {
  return (
    <>
      <DialogText>
        The last plan is restored as a new plan and applies at the next scheduler tick. Each of {name}
        &apos;s own sources that wasn&apos;t kept running by another plan gets a gap “Not scanned while the person was
        retired”, and archived items are rechecked for {name}.
      </DialogText>
      <label className="mt-3 flex items-start gap-2 text-ui">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={catchUp}
          onChange={(e) => {
            setCatchUp(e.target.checked);
          }}
        />
        <span>
          <b className="font-strong">Catch up on the interval since retirement</b>
          <small className="block text-sm text-muted">
            {catchUp
              ? "Bounded by the backfill limit; the gap stays open until the catch-up reads it."
              : "Without catch-up each gap needs someone and creates a task."}
          </small>
        </span>
      </label>
    </>
  );
}

export function LifecycleDialog({
  profile,
  kind,
  onClose,
}: {
  profile: ProfileDetail;
  kind: LifecycleKind;
  onClose: () => void;
}) {
  const toast = useToast();
  const invalidation = useProfileInvalidation();
  const navigate = useAppNavigate();
  const reason = useReason();
  const [catchUp, setCatchUp] = useState(true);
  const mutation = useCommandMutation({
    run: async (csrf, text: string) => {
      const body = { base_revision: profile.revision, reason: text };
      if (kind === "retire") await profileCommands.retire(profile.id, body, csrf);
      else if (kind === "reactivate")
        await profileCommands.reactivate(profile.id, { ...body, catch_up: catchUp }, csrf);
      else await profileCommands.deleteDraft(profile.id, body, csrf);
    },
    ...invalidation,
    onSuccess: () => {
      toast.show(TEXT[kind].done(profile.name));
      onClose();
      if (kind === "delete") navigate("/profiles");
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={TEXT[kind].title(profile.name)}
      onSubmit={() => {
        if (reason.submit()) mutation.mutate(reason.trimmed);
      }}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant={kind === "delete" ? "danger" : "primary"}
            aria-busy={mutation.isPending || undefined}
          >
            {mutation.isError ? "Try again" : TEXT[kind].action}
          </Button>
        </>
      }
    >
      {kind === "retire" ? (
        <DialogText>
          Monitoring stops; shared sources keep running for others. Items, attributions, identity and history are kept,
          and the eligibility of communications already processed doesn&apos;t change. The profile becomes read-only and
          is hidden from the roster by default.
        </DialogText>
      ) : null}
      {kind === "reactivate" ? <ReactivateText name={profile.name} catchUp={catchUp} setCatchUp={setCatchUp} /> : null}
      {kind === "delete" ? (
        <DialogText>
          Soft delete: hidden from all views, searches and exports, and restorable by an Admin in Settings › Deleted
          items. Suggested sources found for it are discarded.
        </DialogText>
      ) : null}
      <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
      <FailedCallout error={mutation.error} />
    </Dialog>
  );
}
